import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { noddiToast, usePagination } from 'noddi-ui-web';
import { useState } from 'react';
import { buildDriverChange, driverChangeMessage, toCarManagerPatches } from './driverChangePlan';
import { CarGroup, MemberGroup } from './notificationGroupTypes';
import { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { useDriverChange } from './useDriverChange';

/** Choose, then agree to what the choice does. */
export type DriverChangeStage = 'choose' | 'review';

export type MemberCarsFlow = ReturnType<typeof useMemberCarsFlow>;

/**
 * The member direction's state, held here rather than inside the picker so the sheet
 * that owns the footer can drive the same steps. Buttons that moved between the body
 * and the footer as the stage changed were the worst part of the old layout.
 */
export const useMemberCarsFlow = ({
  userGroupId,
  member,
  translations: t,
  onSaved
}: {
  userGroupId: number;
  member: MemberGroup;
  translations: NotificationGroupOverviewTranslations;
  /** Hands the sheet back to the member view, where the updated car list is. */
  onSaved: () => void;
}) => {
  const [stage, setStage] = useState<DriverChangeStage>('choose');
  const { pagination, setPagination, setPageIndex, search, setSearch } = usePagination();
  const [pickedCars, setPickedCars] = useState<CarGroup[]>([]);
  const {
    data: cars,
    isFetching,
    error: carsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedNotificationOverviewCars,
    input: {
      userGroupId,
      ...pagination,
      excludeManagerUserGroupMemberIds: [member.userGroupMemberId]
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  const { apply, isPending: isSaving, error } = useDriverChange({ userGroupId });

  // Only what this member can be given. The cars they already drive cannot be taken
  // back here — a car always has exactly one driver — so a locked, ticked section of
  // them at the top of the picker only looked like a control that had stopped working.
  const availableCars = cars?.results ?? [];
  const drivenCount = member.managedCarCount;

  // Each paginated car record carries its current driver, so the review can name
  // who loses the car without loading the full fleet.
  const changes = pickedCars.map((car) => buildDriverChange({ car, incoming: member }));

  const confirm = async () => {
    if (changes.length === 0 || !(await apply(toCarManagerPatches(changes)))) {
      return;
    }
    noddiToast.success(driverChangeMessage(changes, t));
    setPickedCars([]);
    // The assigned cars leave this list (it excludes the member's own), which can empty the page it was on.
    setPageIndex(0);
    setStage('choose');
    onSaved();
  };

  return {
    stage,
    goToChoose: () => setStage('choose'),
    goToReview: () => setStage('review'),
    search,
    setSearch,
    pagination,
    setPagination,
    totalCount: cars?.count,
    isFetching,
    availableCars,
    drivenCount,
    pickedCarIds: pickedCars.map((car) => car.carId),
    togglePicked: (car: CarGroup) =>
      setPickedCars((current) =>
        current.some((picked) => picked.carId === car.carId)
          ? current.filter((picked) => picked.carId !== car.carId)
          : [...current, car]
      ),
    changes,
    confirm: () => void confirm(),
    isSaving,
    error,
    carsError
  };
};
